import { useCallback, useRef } from 'react'

/**
 * Custom hook to handle swipe gestures for mobile sidebar
 * Detects right swipe gestures to open sidebar on mobile devices
 * 
 * @param {Function} onSwipeRight - Callback function when right swipe is detected
 * @param {Object} options - Configuration options
 * @returns {Object} Touch event handlers and refs
 */
export const useSwipeGesture = (onSwipeRight, options = {}) => {
  const {
    threshold = 50, // Minimum distance for swipe
    maxVerticalDistance = 100, // Maximum vertical movement allowed
    minHorizontalDistance = 30, // Minimum horizontal distance for swipe
    enabled = true, // Whether swipe detection is enabled
    preventDefault = true // Whether to prevent default touch behavior
  } = options

  // Check if any modals or forms are currently open
  const isModalOrFormOpen = useCallback(() => {
    // Check for high z-index modals that should block swiping
    const modalSelectors = [
      '.fixed.inset-0.z-[99999]', // Very high z-index modals
      '.fixed.inset-0.z-[100000]' // Very high z-index modals
    ]
    
    // Check if any modal elements are visible
    for (const selector of modalSelectors) {
      const elements = document.querySelectorAll(selector)
      for (const element of elements) {
        const style = window.getComputedStyle(element)
        if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
          return true
        }
      }
    }
    
    // Check for form-focused states (when user is actively filling forms)
    const activeElement = document.activeElement
    if (activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      activeElement.getAttribute('contenteditable') === 'true'
    )) {
      return true
    }
    
    return false
  }, [])

  const touchStartRef = useRef(null)
  const touchEndRef = useRef(null)

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return
    
    // Debug: Check if modal/form detection is blocking
    const modalOpen = isModalOrFormOpen()
    if (process.env.NODE_ENV === 'development') {
      console.log('[SwipeGesture] Touch start - enabled:', enabled, 'modalOpen:', modalOpen)
    }
    
    if (modalOpen) return
    
    const touch = e.touches[0]
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    }
    
    // Add visual feedback for left half swipes
    if (touch.clientX < window.innerWidth / 2) {
      // Add a subtle visual indicator that swipe is possible
      document.body.style.cursor = 'grab'
    }
  }, [enabled, isModalOrFormOpen])

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return
    
    const touch = e.touches[0]
    touchEndRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    }
  }, [enabled])

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !touchStartRef.current || !touchEndRef.current) return

    const start = touchStartRef.current
    const end = touchEndRef.current
    
    const deltaX = end.x - start.x
    const deltaY = end.y - start.y
    const deltaTime = end.time - start.time
    
    // Check if it's a valid swipe gesture
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY)
    const isRightSwipe = deltaX > minHorizontalDistance
    const isWithinVerticalLimit = Math.abs(deltaY) < maxVerticalDistance
    const isQuickSwipe = deltaTime < 500 // Maximum 500ms for swipe
    
    if (isHorizontalSwipe && isRightSwipe && isWithinVerticalLimit && isQuickSwipe) {
    // Check if swipe started from the left half of the screen (more accessible)
    const isFromLeftHalf = start.x < window.innerWidth / 2 // Within left half of screen
      
      if (isFromLeftHalf) {
        if (preventDefault) {
          e.preventDefault()
        }
        onSwipeRight()
      }
    }
    
    // Reset touch references and cursor
    touchStartRef.current = null
    touchEndRef.current = null
    document.body.style.cursor = ''
  }, [enabled, threshold, maxVerticalDistance, minHorizontalDistance, preventDefault, onSwipeRight])

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd
  }
}

export default useSwipeGesture
